import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { hostError, hostsApi } from '../api'
import { useNow } from '../hooks'
import { useSession } from '../session'
import { Button, Notice } from '../components/ui'
import { TOTP_KEY, clock, hostErrorText } from './provisioning'

/*
 * The admin's second factor for every request of the hosts tag
 * (docs/m2-provisioning.md §6). A request goes out without a code; on 403
 * totp_required a window asks for the 6-digit code and repeats the same
 * request with it. In that window: totp_invalid shows the error and asks
 * again; 429 totp_locked shows a countdown from retry_after_s (then the
 * code can be tried again); 403 totp_not_enrolled explains that the secret
 * is issued only on the center, with the admin's id, and offers no form.
 * Any other error closes the window and goes to the caller.
 *
 * The code lives only in the window's state and in the one request it is
 * sent with: never in the query cache, storage or logs, gone on close.
 */

/** The admin closed the window: the caller shows nothing more. */
export class FactorCancelled extends Error {
  constructor() {
    super('cancelled')
  }
}

type Run = <T>(send: (totp?: string) => Promise<T>) => Promise<T>

interface Factor {
  run: Run
  /** confirmations locked until then (ms), 0 = not locked */
  lockedUntil: number
}

const Ctx = createContext<Factor | null>(null)

type Mode = 'code' | 'locked' | 'not_enrolled'

interface Ask {
  mode: Mode
  error: string
  busy: boolean
  /** locked: until when (ms) */
  until: number
  submit: (code: string) => void
  cancel: () => void
}

export function SecondFactorProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [ask, setAsk] = useState<Ask | null>(null)
  const [lockedUntil, setLockedUntil] = useState(0)

  const run: Run = useCallback(
    async <T,>(send: (totp?: string) => Promise<T>): Promise<T> => {
      let first: Mode = 'code'
      let until = 0
      try {
        return await send()
      } catch (e) {
        const h = hostError(e)
        if (h?.code === 'totp_locked') {
          first = 'locked'
          until = Date.now() + (h.retry_after_s ?? 900) * 1000
          setLockedUntil(until)
        } else if (h?.code === 'totp_not_enrolled') {
          first = 'not_enrolled'
        } else if (h?.code !== 'totp_required') {
          throw e
        }
      }
      // The window stays until a code works, the admin closes it, or another error ends it.
      return new Promise<T>((resolve, reject) => {
        const done = () => setAsk(null)
        const submit = async (code: string) => {
          setAsk((a) => a && { ...a, busy: true, error: '' })
          try {
            const r = await send(code)
            done()
            setLockedUntil(0)
            void qc.invalidateQueries({ queryKey: TOTP_KEY })
            resolve(r)
          } catch (e) {
            const h = hostError(e)
            if (h?.code === 'totp_invalid' || h?.code === 'totp_required') {
              setAsk((a) => a && { ...a, mode: 'code', busy: false, error: hostErrorText(e) })
            } else if (h?.code === 'totp_locked') {
              const u = Date.now() + (h.retry_after_s ?? 900) * 1000
              setLockedUntil(u)
              setAsk((a) => a && { ...a, mode: 'locked', until: u, busy: false, error: '' })
            } else if (h?.code === 'totp_not_enrolled') {
              setAsk((a) => a && { ...a, mode: 'not_enrolled', busy: false, error: '' })
            } else {
              done()
              reject(e)
            }
          }
        }
        setAsk({ mode: first, until, error: '', busy: false, submit: (c) => void submit(c), cancel: () => (done(), reject(new FactorCancelled())) })
      })
    },
    [qc],
  )

  return (
    <Ctx.Provider value={{ run, lockedUntil }}>
      {children}
      {ask && <FactorDialog ask={ask} />}
    </Ctx.Provider>
  )
}

/** Runs a request that may need the second factor: send(totp?) is repeated with the code. */
export function useSecondFactor(): Run {
  const r = useContext(Ctx)
  if (!r) throw new Error('useSecondFactor outside SecondFactorProvider')
  return r.run
}

/** The command that issues the secret, for this admin. */
export const enrollCommand = (id?: number) => `controlapi provisioner totp-enroll --admin ${id ?? '<your admin id>'}`

function FactorDialog({ ask }: { ask: Ask }) {
  const { user } = useSession()
  const now = useNow(1000)
  const [code, setCode] = useState('')
  const input = useRef<HTMLInputElement>(null)
  const left = ask.until - now
  const mode: Mode = ask.mode === 'locked' && left <= 0 ? 'code' : ask.mode
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && ask.cancel()
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [ask])
  useEffect(() => {
    // A wrong code is wiped from the field; the field gets the focus back.
    setCode('')
    if (mode === 'code') input.current?.focus()
  }, [ask.error, mode])
  const ok = /^\d{6}$/.test(code)
  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-bg/80">
      <div className="flex min-h-full items-center justify-center p-4">
        <form
          role="dialog"
          aria-modal="true"
          aria-labelledby="totp-title"
          data-testid="totp-dialog"
          data-mode={mode}
          className="rs-corners w-full max-w-sm space-y-3 rounded-lg border border-line-2 bg-panel p-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (mode === 'code' && ok && !ask.busy) {
              const c = code
              setCode('')
              ask.submit(c)
            }
          }}
        >
          <h2 id="totp-title" className="text-lg font-semibold">
            {mode === 'not_enrolled' ? 'Second factor not set up' : mode === 'locked' ? 'Confirmations locked' : 'Confirm with your code'}
          </h2>
          {mode === 'not_enrolled' && (
            <div data-testid="totp-not-enrolled-help" className="space-y-2 text-sm text-text-soft">
              <p>Node actions need a second factor. Its secret is issued only on the center's server, never through the web console. Run there:</p>
              <code className="block rounded-md border border-line bg-bg px-3 py-2 font-mono text-xs break-all text-text">{enrollCommand(user?.id)}</code>
              <p className="text-xs text-muted">
                Your admin id is <span className="num font-mono text-text-soft">{user?.id ?? '—'}</span>. Scan the otpauth:// link it prints with your authenticator app, then try again.
                Status and diagnostics work without it.
              </p>
            </div>
          )}
          {mode === 'locked' && (
            <div data-testid="totp-locked" className="space-y-1 text-sm text-text-soft">
              <p>Too many wrong codes in a row. Node actions that need a code are locked for you.</p>
              <p>
                Try again in{' '}
                <span data-testid="totp-locked-left" className="num font-mono text-warn">
                  {clock(left)}
                </span>
                .
              </p>
            </div>
          )}
          {mode === 'code' && (
            <>
              <p className="text-sm text-text-soft">Enter the 6-digit code from your authenticator app. One code covers your node actions for the next minute.</p>
              <label className="block">
                <span className="mb-1 block text-sm font-medium">Code</span>
                <input
                  ref={input}
                  data-testid="totp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="num w-full rounded-md border border-line-2 bg-bg px-3 py-2 text-center font-mono text-xl tracking-[0.4em] text-text focus:border-accent focus:shadow-focus focus:outline-none"
                />
              </label>
              <div data-testid="totp-error">
                <Notice>{ask.error}</Notice>
              </div>
            </>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="quiet" type="button" data-testid="totp-cancel" onClick={ask.cancel}>
              {mode === 'code' ? 'Cancel' : 'Close'}
            </Button>
            {mode === 'code' && (
              <Button variant="primary" type="submit" disabled={!ok || ask.busy} data-testid="totp-submit">
                {ask.busy ? 'Checking…' : 'Confirm'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

/**
 * The header of the section: "Confirmed · N s left" while the last code
 * covers commands, "Second factor not set up" without one, the lock's
 * countdown after too many wrong codes; nothing otherwise.
 */
export function FactorBadge({ enabled = true }: { enabled?: boolean }) {
  const f = useContext(Ctx)
  const q = useQuery({ queryKey: TOTP_KEY, queryFn: hostsApi.totp, enabled, retry: false, refetchInterval: 30_000 })
  const now = useNow(1000)
  const locked = (f?.lockedUntil ?? 0) - now
  if (locked > 0) {
    return (
      <span data-testid="totp-badge-locked" className="num rounded-sm border border-fail/50 px-2 py-0.5 text-xs text-fail">
        2FA locked · {clock(locked)}
      </span>
    )
  }
  if (q.data && !q.data.enrolled) {
    return (
      <span data-testid="totp-not-enrolled" className="text-xs text-warn" title={`On the center: ${enrollCommand()}`}>
        ▲ Second factor not set up
      </span>
    )
  }
  const until = q.data?.confirmed_until ? Date.parse(q.data.confirmed_until) : 0
  const left = Math.ceil((until - now) / 1000)
  if (left <= 0) return null
  return (
    <span data-testid="totp-confirmed" className="num rounded-sm border border-live/50 px-2 py-0.5 text-xs text-live" title="Node actions need no new code until then">
      Confirmed · {left} s left
    </span>
  )
}
